/* eslint-disable no-magic-numbers -- crypto constants and buffer sizes */
/* eslint-disable @typescript-eslint/class-methods-use-this -- crypto methods use crypto.subtle, not this */
import { type KEM, ml_kem768 } from '@noble/post-quantum/ml-kem';
import argon2 from 'argon2-browser/dist/argon2-bundled.min.js';

import {
  AES_KEY_SIZE_BYTES,
  AES_KEY_SIZE_BITS,
  AES_IV_SIZE_BYTES,
  AES_AUTH_TAG_SIZE_BYTES,
  AES_AUTH_TAG_SIZE_BITS,
  ARGON2_TIME_COST,
  ARGON2_MEMORY_COST,
  ARGON2_HASH_LENGTH,
  ARGON2_PARALLELISM,
  HEX_RADIX,
  MLKEM_SEED_SIZE_BYTES,
} from '@/constants/crypto.constants';
import type { LoggerService } from '@/services/abstract/LoggerService';
import type { Logger } from '@/types/Logger';
import { bytesToHex } from '@/utils/hex.utils';

import {
  CryptoService,
  type MLKEMKeyPair,
  type EncapsulationResult,
  type DecapsulationResult,
  type PasswordHashResult,
  type HashResult,
  type AesGcmEncryptionResult
} from '../abstract/poc/crypto.service.interface';

export class CryptoServiceImpl extends CryptoService {
  private readonly mlkem: KEM;
  private readonly log: Logger;

  constructor(loggerService: LoggerService, mlkem: KEM = ml_kem768) {
    super();
    this.mlkem = mlkem;
    this.log = loggerService.createLogger({ namespace: 'six3ro:crypto' });
  }

  // ============= KEY GENERATION =============

  public generateAesKey(): string {
    try {
      const randomBytes = this.randomBytes(AES_KEY_SIZE_BYTES);
      return this.bytesToHex(randomBytes);
    } catch (error) {
      return CryptoServiceImpl.handleCryptoError('AES key generation', error);
    }
  }

  public async generateMlKemKeyPair(seed?: Uint8Array): Promise<MLKEMKeyPair> {
    try {
      const keypair = this.mlkem.keygen(seed);
      return await Promise.resolve({
        privateKey: keypair.secretKey,
        publicKey: keypair.publicKey
      });
    } catch (error) {
      CryptoServiceImpl.handleCryptoError('ML-KEM key pair generation', error);
    }
  }

  public async generateMlKemKeyPairFromCredentials(
    email: string,
    password: string
  ): Promise<MLKEMKeyPair> {
    try {
      const passwordHashResult = await this.hashPassword(password, email);
      const argon2Hash = passwordHashResult.hash;
      const sha512Result = await this.hashSha512(this.bytesToHex(argon2Hash));

      const seed = new Uint8Array(MLKEM_SEED_SIZE_BYTES);
      seed.set(argon2Hash, 0);
      seed.set(sha512Result.hash.slice(0, 32), 32);

      return await this.generateMlKemKeyPair(seed);
    } catch (error) {
      CryptoServiceImpl.handleCryptoError('ML-KEM key pair generation from credentials', error);
    }
  }

  // ============= KEY ENCAPSULATION =============

  public encapsulateMlKem(
    publicKey: Uint8Array,
    seed?: Uint8Array
  ): EncapsulationResult {
    try {
      const result = this.mlkem.encapsulate(publicKey, seed);
      return {
        cipherText: result.cipherText,
        sharedSecret: result.sharedSecret
      };
    } catch (error) {
      return CryptoServiceImpl.handleCryptoError('ML-KEM encapsulation', error);
    }
  }

  public decapsulateMlKem(
    cipherText: Uint8Array,
    privateKey: Uint8Array
  ): DecapsulationResult {
    try {
      const sharedSecret = this.mlkem.decapsulate(cipherText, privateKey);
      return {
        cipherText,
        sharedSecret
      };
    } catch (error) {
      return CryptoServiceImpl.handleCryptoError('ML-KEM decapsulation', error);
    }
  }

  // ============= SYMMETRIC ENCRYPTION =============

  public async encrypt(
    data: Uint8Array,
    key: Uint8Array
  ): Promise<AesGcmEncryptionResult> {
    try {
      const { iv, cryptoKey } = await CryptoServiceImpl.prepareEncryption(key);
      const encryptedBuffer = await CryptoServiceImpl.performEncryption(data, cryptoKey, iv);
      return CryptoServiceImpl.extractEncryptionComponents(encryptedBuffer, iv);
    } catch (error) {
      return CryptoServiceImpl.handleCryptoError('AES-GCM encryption', error);
    }
  }

  public async decrypt(
    encryptedData: Uint8Array,
    iv: Uint8Array,
    authTag: Uint8Array,
    key: Uint8Array
  ): Promise<Uint8Array> {
    try {
      const cryptoKey = await CryptoServiceImpl.prepareCryptoKey(key, ['decrypt']);
      const combinedData = CryptoServiceImpl.combineEncryptedDataWithTag(encryptedData, authTag);
      const decryptedBuffer = await CryptoServiceImpl.performDecryption(combinedData, cryptoKey, iv);
      return new Uint8Array(decryptedBuffer);
    } catch (error) {
      return CryptoServiceImpl.handleCryptoError('AES-GCM decryption', error);
    }
  }

  // ============= HASHING =============

  public async hashSha512(input: string): Promise<HashResult> {
    try {
      const encoder = new TextEncoder();
      const data = encoder.encode(input);
      const hashBuffer = await crypto.subtle.digest('SHA-512', data);
      const hashArray = new Uint8Array(hashBuffer);
      const hexString = CryptoServiceImpl.convertToHexString(hashArray);

      return {
        hash: hashArray,
        hexString
      };
    } catch (error) {
      return CryptoServiceImpl.handleCryptoError('SHA-512 hashing', error);
    }
  }

  public async encryptPrivateKey(
    privateKey: Uint8Array,
    ukek: PasswordHashResult
  ): Promise<string> {
    const { encryptedData, iv, authTag } = await this.encryptWithAesGcm(privateKey, ukek.hash);
    const result = new Uint8Array(iv.length + encryptedData.byteLength + authTag.length);
    result.set(iv);
    result.set(new Uint8Array(encryptedData), iv.length);
    result.set(authTag, iv.length + encryptedData.byteLength);

    return CryptoServiceImpl.convertToHexString(result);
  }

  public async decryptPrivateKey(encryptedPrivateKeyHex: string, ukekHash: Uint8Array): Promise<Uint8Array> {
    try {
      this.log.debug('Attempting private key decryption...');
      this.log.debug('Input hex length:', encryptedPrivateKeyHex.length);
      this.log.debug('UKEK hash length:', ukekHash.length);

      const encryptedBytes = new Uint8Array(
        encryptedPrivateKeyHex.match(/.{1,2}/g)?.map(byte => Number.parseInt(byte, HEX_RADIX)) ?? []
      );
      this.log.debug('Encrypted bytes length:', encryptedBytes.length);

      const IV_LENGTH_12 = 12;  // Current standard
      const IV_LENGTH_16 = 16;  // Legacy format
      const AUTH_TAG_LENGTH = 16;

      this.log.debug('Format analysis: Total bytes:', encryptedBytes.length);

      const possibleFormats = [
        { ivLength: IV_LENGTH_12, name: '12-byte IV (current)' },
        { ivLength: IV_LENGTH_16, name: '16-byte IV (legacy)' }
      ];

      for (const format of possibleFormats) {
        const minLengthWithAuthTag = format.ivLength + AUTH_TAG_LENGTH + 1;

        this.log.debug(`Trying ${format.name} - Min length:`, minLengthWithAuthTag);

        if (encryptedBytes.length >= minLengthWithAuthTag) {
          this.log.debug(`Attempting new format with ${format.name}...`);
          try {
            const iv = encryptedBytes.slice(0, format.ivLength);
            const authTag = encryptedBytes.slice(-AUTH_TAG_LENGTH);
            const encryptedData = encryptedBytes.slice(format.ivLength, -AUTH_TAG_LENGTH);

            this.log.debug('Components:', { iv: iv.length, data: encryptedData.length, authTag: authTag.length });

            const result = await this.decrypt(encryptedData, iv, authTag, ukekHash);
            this.log.info(`New format decryption succeeded with ${format.name}`);
            return result;
          } catch (newFormatError) {
            this.log.warn(`New format with ${format.name} failed:`, newFormatError);
          }
        }

        if (encryptedBytes.length >= format.ivLength + 1) {
          this.log.debug(`Attempting legacy format with ${format.name}...`);

          const iv = encryptedBytes.slice(0, format.ivLength);
          const encryptedData = encryptedBytes.slice(format.ivLength);

          this.log.debug('Legacy components:', { iv: iv.length, data: encryptedData.length });

          try {
            const result = await this.decryptLegacyFormat(encryptedData, iv, ukekHash);
            this.log.info(`Legacy format decryption succeeded with ${format.name}`);
            return result;
          } catch (legacyError) {
            this.log.error(`Legacy format with ${format.name} failed:`, legacyError);
          }
        }
      }

      throw new Error('All decryption formats failed - invalid encrypted private key data');

    } catch (error) {
      return CryptoServiceImpl.handleCryptoError('Private key decryption', error);
    }
  }

  // ============= PASSWORD OPERATIONS =============

  public async hashPassword(password: string, emailForSalt: string): Promise<PasswordHashResult> {
    try {
      const { hash: salt, hexString: sha512Hash } = await this.hashSha512(password + emailForSalt);
      const argon2Result = await CryptoServiceImpl.performArgon2Hashing(password, salt);
      const keyHex = CryptoServiceImpl.convertToHexString(argon2Result.hash);

      return {
        sha512Hash,
        keyHex,
        encoded: argon2Result.encoded,
        hash: argon2Result.hash
      };
    } catch (error) {
      return CryptoServiceImpl.handleCryptoError('Argon2 password hashing', error);
    }
  }

  public async deriveKey(password: string, email: string): Promise<string> {
    try {
      const result = await this.hashPassword(password, email);
      return result.keyHex;
    } catch (error) {
      return CryptoServiceImpl.handleCryptoError('key derivation from password', error);
    }
  }

  // ============= UTILITIES =============

  public bytesToHex(data: Uint8Array): string {
    try {
      return CryptoServiceImpl.convertToHexString(data);
    } catch (error) {
      return CryptoServiceImpl.handleCryptoError('Uint8Array to hex conversion', error);
    }
  }

  public randomBytes(length: number): Uint8Array {
    try {
      return crypto.getRandomValues(new Uint8Array(length));
    } catch (error) {
      return CryptoServiceImpl.handleCryptoError('random bytes generation', error);
    }
  }

  // ============= PRIVATE HELPER METHODS =============

  private async encryptWithAesGcm(
    data: Uint8Array,
    key: Uint8Array
  ): Promise<AesGcmEncryptionResult> {
    const iv = crypto.getRandomValues(new Uint8Array(AES_IV_SIZE_BYTES));
    const keyBuffer = new Uint8Array(key);
    const dataBuffer = new Uint8Array(data);

    const cryptoKey = await crypto.subtle.importKey(
      'raw',
      keyBuffer,
      { name: 'AES-GCM', length: AES_KEY_SIZE_BITS },
      false,
      ['encrypt']
    );

    const encryptedBuffer = await crypto.subtle.encrypt(
      { name: 'AES-GCM', iv, tagLength: AES_AUTH_TAG_SIZE_BITS },
      cryptoKey,
      dataBuffer
    );

    const encryptedArray = new Uint8Array(encryptedBuffer);
    const authTag = encryptedArray.slice(encryptedArray.length - AES_AUTH_TAG_SIZE_BYTES);
    const encryptedData = encryptedArray.slice(0, encryptedArray.length - AES_AUTH_TAG_SIZE_BYTES);

    return { encryptedData, iv, authTag };
  }

  private async decryptLegacyFormat(encryptedData: Uint8Array, iv: Uint8Array, key: Uint8Array): Promise<Uint8Array> {
    try {
      this.log.debug('Legacy decryption starting', { data: encryptedData.length, iv: iv.length, key: key.length });

      const keyBuffer = new ArrayBuffer(key.length);
      const keyView = new Uint8Array(keyBuffer);
      keyView.set(key);

      const ivBuffer = new ArrayBuffer(iv.length);
      const ivView = new Uint8Array(ivBuffer);
      ivView.set(iv);

      const dataBuffer = new ArrayBuffer(encryptedData.length);
      const dataView = new Uint8Array(dataBuffer);
      dataView.set(encryptedData);

      this.log.debug('ArrayBuffers created', { key: keyBuffer.byteLength, iv: ivBuffer.byteLength, data: dataBuffer.byteLength });

      const cryptoKey = await crypto.subtle.importKey(
        'raw',
        keyBuffer,
        { name: 'AES-GCM', length: AES_KEY_SIZE_BITS },
        false,
        ['decrypt']
      );

      const decryptedBuffer = await crypto.subtle.decrypt(
        {
          name: 'AES-GCM',
          iv: ivBuffer,
          tagLength: AES_AUTH_TAG_SIZE_BITS
        },
        cryptoKey,
        dataBuffer
      );

      return new Uint8Array(decryptedBuffer);
    } catch (error) {
      throw new Error(`Legacy format decryption failed: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  }

  private static handleCryptoError(operation: string, error: unknown): never {
    const message = error instanceof Error ? error.message : 'Unknown error';
    throw new Error(`${operation} failed: ${message}`);
  }

  private static async prepareEncryption(key: Uint8Array) {
    const iv = crypto.getRandomValues(new Uint8Array(AES_IV_SIZE_BYTES));
    const cryptoKey = await CryptoServiceImpl.prepareCryptoKey(key, ['encrypt']);
    return { iv, cryptoKey };
  }

  private static async prepareCryptoKey(key: Uint8Array, keyUsages: KeyUsage[]): Promise<CryptoKey> {
    const keyBuffer = new ArrayBuffer(key.length);
    const keyView = new Uint8Array(keyBuffer);
    keyView.set(key);

    return await crypto.subtle.importKey(
      'raw',
      keyBuffer,
      { name: 'AES-GCM', length: AES_KEY_SIZE_BITS },
      false,
      keyUsages
    );
  }

  private static async performEncryption(data: Uint8Array, cryptoKey: CryptoKey, iv: Uint8Array): Promise<ArrayBuffer> {
    const dataBuffer = new ArrayBuffer(data.length);
    const dataView = new Uint8Array(dataBuffer);
    dataView.set(data);

    const ivBuffer = new ArrayBuffer(iv.length);
    const ivView = new Uint8Array(ivBuffer);
    ivView.set(iv);

    return await crypto.subtle.encrypt(
      { name: 'AES-GCM', iv: ivBuffer, tagLength: AES_AUTH_TAG_SIZE_BITS },
      cryptoKey,
      dataBuffer
    );
  }

  private static extractEncryptionComponents(encryptedBuffer: ArrayBuffer, iv: Uint8Array): AesGcmEncryptionResult {
    const encryptedArray = new Uint8Array(encryptedBuffer);
    const authTag = encryptedArray.slice(
      encryptedArray.length - AES_AUTH_TAG_SIZE_BYTES
    );
    const encryptedData = encryptedArray.slice(
      0,
      encryptedArray.length - AES_AUTH_TAG_SIZE_BYTES
    );

    return { encryptedData, iv, authTag };
  }

  private static combineEncryptedDataWithTag(encryptedData: Uint8Array, authTag: Uint8Array): ArrayBuffer {
    const combined = new ArrayBuffer(encryptedData.length + authTag.length);
    const combinedView = new Uint8Array(combined);
    combinedView.set(encryptedData, 0);
    combinedView.set(authTag, encryptedData.length);
    return combined;
  }

  private static async performDecryption(combinedData: ArrayBuffer, cryptoKey: CryptoKey, iv: Uint8Array): Promise<ArrayBuffer> {
    const ivBuffer = new ArrayBuffer(iv.length);
    const ivView = new Uint8Array(ivBuffer);
    ivView.set(iv);

    return await crypto.subtle.decrypt(
      { name: 'AES-GCM', iv: ivBuffer, tagLength: AES_AUTH_TAG_SIZE_BITS },
      cryptoKey,
      combinedData
    );
  }

  private static async performArgon2Hashing(password: string, salt: Uint8Array) {
    return await argon2.hash({
      pass: password,
      salt,
      type: argon2.ArgonType.Argon2id,
      time: ARGON2_TIME_COST,
      mem: ARGON2_MEMORY_COST,
      hashLen: ARGON2_HASH_LENGTH,
      parallelism: ARGON2_PARALLELISM,
    });
  }

  private static convertToHexString(data: Uint8Array): string {
    return bytesToHex(data);
  }

}
